import { useSyncExternalStore, useCallback } from "react";
import { DEFAULT_QUOTES, DEFAULT_AFFIRMATIONS } from "./quotes";

const KEY = "tempo-leggero:v1";

const DEFAULT_CATEGORIES = [
  { id: "lavoro", name: "Lavoro", color: "#9BA3B5" },
  { id: "casa", name: "Casa", color: "#CBA38D" },
  { id: "personale", name: "Personale", color: "#D4B5B0" },
  { id: "salute", name: "Salute", color: "#A3B899" },
  { id: "altro", name: "Altro", color: "#D9C5B2" }
];

const DEFAULT_STATE = {
  tasks: [],
  categories: DEFAULT_CATEGORIES,
  settings: {
    name: "",
    gridStart: 6,
    gridEnd: 23,
    quotes: DEFAULT_QUOTES,
    affirmations: DEFAULT_AFFIRMATIONS
  },
  journal: {}, // { [YYYY-MM-DD]: { best: "", learned: "" } }
  timer: null  // { taskId, startedAt } or null
};

let state = load();
const listeners = new Set();

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    // Merge in defaults for missing keys
    return {
      ...DEFAULT_STATE,
      ...parsed,
      settings: { ...DEFAULT_STATE.settings, ...(parsed.settings || {}) },
      categories: parsed.categories && parsed.categories.length ? parsed.categories : DEFAULT_CATEGORIES,
      journal: parsed.journal || {}
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function persist() {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (_e) { /* noop */ }
}

function emit() {
  persist();
  listeners.forEach((l) => l());
  try { window.dispatchEvent(new Event("tl:store-changed")); } catch {}
}

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useStore(selector = (s) => s) {
  const get = useCallback(() => selector(state), [selector]);
  return useSyncExternalStore(subscribe, get, get);
}

export function getState() { return state; }

// Overwrite state from a remote payload (used after login sync)
export function hydrateFromRemote(remote) {
  if (!remote) return;
  state = {
    ...DEFAULT_STATE,
    ...state,
    tasks: Array.isArray(remote.tasks) ? remote.tasks : state.tasks,
    categories: Array.isArray(remote.categories) && remote.categories.length ? remote.categories : state.categories,
    settings: { ...DEFAULT_STATE.settings, ...(remote.settings || {}) },
    journal: remote.journal || {}
  };
  emit();
}

// ---- Mutations ----
function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export function addTask(input) {
  const t = {
    id: uid(),
    title: (input.title || "").trim(),
    categoryId: input.categoryId || state.categories[0]?.id || "altro",
    date: input.date || null,          // YYYY-MM-DD or null (Prossimamente)
    time: input.time || null,          // HH:MM or null
    durationMin: Number(input.durationMin) || 30,
    done: false,
    notes: input.notes || "",
    allDay: !!input.allDay,
    recurrence: input.recurrence || "none", // none | daily | weekly | weekdays
    recurrenceUntil: input.recurrence && input.recurrence !== "none" ? (input.recurrenceUntil || null) : null, // last day (YYYY-MM-DD)
    recurrenceCount: input.recurrence && input.recurrence !== "none" ? (Number(input.recurrenceCount) || null) : null, // total occurrences
    order: Date.now(),
    priority: false,
    timeSpentSec: 0,
    createdAt: new Date().toISOString()
  };
  if (!t.title) return null;
  state = { ...state, tasks: [t, ...state.tasks] };
  emit();
  return t;
}

export function updateTask(id, patch) {
  state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, ...patch } : t) };
  emit();
}

export function toggleTaskDone(id, occurrenceDate) {
  const task = state.tasks.find((t) => t.id === id);
  if (!task) return;
  const isRecurring = task.recurrence && task.recurrence !== "none";
  if (isRecurring && occurrenceDate) {
    const completed = task.completedDates || [];
    const has = completed.includes(occurrenceDate);
    const next = has ? completed.filter((d) => d !== occurrenceDate) : [...completed, occurrenceDate];
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, completedDates: next } : t) };
  } else {
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, done: !t.done, completedAt: !t.done ? new Date().toISOString() : null } : t) };
  }
  emit();
}

// scope for recurring tasks: "single" (this day) | "future" (this day and after) | "all" (whole series)
export function deleteTask(id, occurrenceDate, scope = "single") {
  const task = state.tasks.find((t) => t.id === id);
  if (!task) return null;
  const isRecurring = task.recurrence && task.recurrence !== "none";
  if (isRecurring && occurrenceDate && scope === "single") {
    const excluded = task.excludedDates || [];
    if (excluded.includes(occurrenceDate)) return null;
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, excludedDates: [...excluded, occurrenceDate] } : t) };
    emit();
    return { kind: "occurrence", taskId: id, occurrenceDate };
  }
  if (isRecurring && occurrenceDate && scope === "future" && task.date && occurrenceDate > task.date) {
    const prev = { recurrenceUntil: task.recurrenceUntil || null, recurrenceCount: task.recurrenceCount || null };
    // Convert a count end into a date so the series has a single, clear last day
    let until = addDaysISO(occurrenceDate, -1);
    if (prev.recurrenceUntil && prev.recurrenceUntil < until) until = prev.recurrenceUntil;
    if (prev.recurrenceCount) {
      const last = nthOccurrenceISO(task, prev.recurrenceCount);
      if (last && last < until) until = last;
    }
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, recurrenceUntil: until, recurrenceCount: null } : t) };
    emit();
    return { kind: "series-end", taskId: id, prev };
  }
  const timer = state.timer;
  const snapshot = { ...task };
  state = { ...state, tasks: state.tasks.filter((t) => t.id !== id), timer: timer && timer.taskId === id ? null : timer };
  emit();
  return { kind: "full", task: snapshot };
}

export function restoreDelete(op) {
  if (!op) return;
  if (op === lastDelete) { lastDelete = null; listeners.forEach((l) => l()); }
  if (op.kind === "series-end") {
    state = { ...state, tasks: state.tasks.map((t) => t.id === op.taskId ? { ...t, ...op.prev } : t) };
    emit();
  } else if (op.kind === "occurrence") {
    state = { ...state, tasks: state.tasks.map((t) => t.id === op.taskId ? { ...t, excludedDates: (t.excludedDates || []).filter((d) => d !== op.occurrenceDate) } : t) };
    emit();
  } else if (op.kind === "full") {
    // Avoid duplicate if somehow it's already back
    if (!state.tasks.find((t) => t.id === op.task.id)) {
      state = { ...state, tasks: [op.task, ...state.tasks] };
      emit();
    }
  }
}

// ---- Undo last delete (only the most recent one, in memory) ----
let lastDelete = null;
export function rememberDelete(op) {
  lastDelete = op || null;
  listeners.forEach((l) => l());
}
export function canUndoDelete() {
  return !!lastDelete;
}
export function undoLastDelete() {
  const op = lastDelete;
  if (!op) return false;
  restoreDelete(op);
  return true;
}

// ---- Timer ----
export function startTimer(id, occurrenceDate = null) {
  // Stop currently running timer if any
  if (state.timer) {
    _flushTimer();
  }
  const task = state.tasks.find((t) => t.id === id);
  const date = isRecurringTask(task) ? occurrenceDate : null;
  state = { ...state, timer: { taskId: id, startedAt: Date.now(), date } };
  emit();
}
export function stopTimer() {
  if (!state.timer) return;
  _flushTimer();
  state = { ...state, timer: null };
  emit();
}
function _flushTimer() {
  const { taskId, startedAt, date } = state.timer;
  const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  state = { ...state, tasks: state.tasks.map((t) => {
    if (t.id !== taskId) return t;
    if (isRecurringTask(t) && date) {
      const byDate = t.spentByDate || {};
      return { ...t, spentByDate: { ...byDate, [date]: (byDate[date] || 0) + elapsedSec } };
    }
    return { ...t, timeSpentSec: (t.timeSpentSec || 0) + elapsedSec };
  }) };
}

const isRecurringTask = (t) => !!t && !!t.recurrence && t.recurrence !== "none";

// Seconds recorded for a task (for recurring: that day only; legacy series total shows on the first day)
export function spentSecFor(t, occurrenceDate) {
  if (!t) return 0;
  if (!isRecurringTask(t)) return t.timeSpentSec || 0;
  const d = occurrenceDate || t.date;
  return ((t.spentByDate || {})[d] || 0) + (d === t.date ? (t.timeSpentSec || 0) : 0);
}

// Minutes to count as "time spent" for an expanded task: timer time if recorded, else planned duration when done
export function workedMin(t) {
  if (t.timeSpentSec > 0) return Math.round(t.timeSpentSec / 60);
  return t.done ? (t.durationMin || 0) : 0;
}

export function isTimerFor(timer, task) {
  if (!timer || !task || timer.taskId === undefined) return false;
  const id = task.id;
  if (timer.taskId !== id) return false;
  return !timer.date || timer.date === (task._occurrenceDate || task.date);
}

// Correct the time actually spent (minutes). A running timer on that same task/day is discarded.
export function setTimeSpent(id, occurrenceDate, minutes) {
  const sec = Math.max(0, Math.round(Number(minutes) || 0) * 60);
  const task = state.tasks.find((t) => t.id === id);
  if (!task) return;
  const running = state.timer && isTimerFor(state.timer, { id, _occurrenceDate: occurrenceDate, date: task.date });
  state = { ...state, timer: running ? null : state.timer, tasks: state.tasks.map((t) => {
    if (t.id !== id) return t;
    if (!isRecurringTask(t)) return { ...t, timeSpentSec: sec };
    const d = occurrenceDate || t.date;
    const next = { ...t, spentByDate: { ...(t.spentByDate || {}), [d]: sec } };
    if (d === t.date) next.timeSpentSec = 0; // legacy total folded into the first day
    return next;
  }) };
  emit();
}

// ---- Priority ----
export function toggleTaskPriority(id, occurrenceDate) {
  const task = state.tasks.find((t) => t.id === id);
  if (!task) return { ok: false, reason: "notfound" };
  const isRecurring = task.recurrence && task.recurrence !== "none";
  const targetDate = occurrenceDate || task.date;
  const currentlyPriority = isRecurring
    ? (task.priorityDates || []).includes(targetDate)
    : !!task.priority;
  if (!currentlyPriority) {
    // Count priorities on that date across all tasks (as they render)
    const count = prioritiesForDate(targetDate).length;
    if (count >= 3) return { ok: false, reason: "limit" };
  }
  if (isRecurring) {
    const dates = task.priorityDates || [];
    const has = dates.includes(targetDate);
    const next = has ? dates.filter((d) => d !== targetDate) : [...dates, targetDate];
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, priorityDates: next } : t) };
  } else {
    state = { ...state, tasks: state.tasks.map((t) => t.id === id ? { ...t, priority: !t.priority } : t) };
  }
  emit();
  return { ok: true };
}

// ---- Recurring occurrences ----
function addDaysISO(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function diffDaysISO(a, b) {
  return Math.round((new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z")) / 86400000);
}

// Patch a single occurrence of a recurring task (stored in `overrides[date]`)
export function updateOccurrence(id, dateISO, patch) {
  if (!dateISO) return;
  state = { ...state, tasks: state.tasks.map((t) => t.id === id
    ? { ...t, overrides: { ...(t.overrides || {}), [dateISO]: { ...((t.overrides || {})[dateISO] || {}), ...patch } } }
    : t) };
  emit();
}

// Turn one occurrence into a standalone task (and skip it in the series)
export function detachOccurrence(id, occurrenceDate, patch = {}) {
  const base = state.tasks.find((t) => t.id === id);
  if (!base || !occurrenceDate) return null;
  const ov = (base.overrides || {})[occurrenceDate] || {};
  const copy = { ...base, ...ov, ...patch };
  copy.id = uid();
  copy.recurrence = "none";
  copy.done = (base.completedDates || []).includes(occurrenceDate);
  copy.priority = (base.priorityDates || []).includes(occurrenceDate);
  copy.timeSpentSec = spentSecFor(base, occurrenceDate);
  delete copy.spentByDate;
  copy.order = Date.now();
  copy.createdAt = new Date().toISOString();
  delete copy.overrides;
  delete copy.excludedDates;
  delete copy.completedDates;
  delete copy.priorityDates;
  delete copy._occurrenceDate;
  delete copy._hasOverride;
  const excluded = base.excludedDates || [];
  state = { ...state, tasks: [copy, ...state.tasks.map((t) => t.id === id
    ? { ...t, excludedDates: excluded.includes(occurrenceDate) ? excluded : [...excluded, occurrenceDate] }
    : t)] };
  emit();
  return copy;
}

// Move/reschedule honouring the recurrence scope ("single" | "all").
// newTime: "HH:MM" -> timed, null -> all-day, undefined -> keep current time.
export function moveTaskSmart(task, occurrenceDate, newDate, newTime, scope = "single") {
  if (!task) return;
  const isRecurring = task.recurrence && task.recurrence !== "none";
  const timePatch = newTime === undefined
    ? {}
    : (newTime ? { time: newTime, allDay: false } : { time: null, allDay: true });
  if (!isRecurring) {
    moveTaskToDate(task.id, newDate, newTime);
    return;
  }
  if (scope === "single") {
    if (!newDate || newDate === occurrenceDate) {
      if (Object.keys(timePatch).length) updateOccurrence(task.id, occurrenceDate, timePatch);
    } else {
      detachOccurrence(task.id, occurrenceDate, { date: newDate, ...timePatch });
    }
    return;
  }
  // "all": never move the series anchor for daily/weekdays (it would hide earlier days)
  const patch = { ...timePatch };
  if (task.recurrence === "weekly" && newDate && occurrenceDate && newDate !== occurrenceDate) {
    patch.date = addDaysISO(task.date, diffDaysISO(occurrenceDate, newDate));
  }
  updateTask(task.id, patch);
}

export function moveTaskToDate(id, newDate, newTime) {
  state = { ...state, tasks: state.tasks.map((t) => {
    if (t.id !== id) return t;
    const patch = { date: newDate };
    if (newTime !== undefined) {
      patch.time = newTime;
      if (newTime) patch.allDay = false;
    }
    return { ...t, ...patch };
  }) };
  emit();
}

// ---- Categories ----
export function addCategory(name, color) {
  const c = { id: uid(), name: name.trim(), color: color || "#B5B5B5" };
  if (!c.name) return null;
  state = { ...state, categories: [...state.categories, c] };
  emit();
  return c;
}
export function updateCategory(id, patch) {
  const known = state.categories.some((c) => c.id === id);
  const categories = known
    ? state.categories.map((c) => c.id === id ? { ...c, ...patch } : c)
    : [...state.categories, { ...findCategory(id), ...patch, orphan: undefined }];
  state = { ...state, categories };
  emit();
}
export function deleteCategory(id) {
  if (state.categories.length <= 1) return;
  state = { ...state, categories: state.categories.filter((c) => c.id !== id) };
  emit();
  try { window.dispatchEvent(new CustomEvent("tl:category-deleted", { detail: { id } })); } catch {}
}
// Adopt the server's merged list (it may contain categories created on other devices)
export function applyServerCategories(list) {
  if (!Array.isArray(list) || !list.length) return;
  if (JSON.stringify(list) === JSON.stringify(state.categories)) return;
  state = { ...state, categories: list };
  emit();
}

// ---- Settings ----
export function updateSettings(patch) {
  state = { ...state, settings: { ...state.settings, ...patch } };
  emit();
}
export function setQuotes(list) {
  state = { ...state, settings: { ...state.settings, quotes: list } };
  emit();
}
export function setAffirmations(list) {
  state = { ...state, settings: { ...state.settings, affirmations: list } };
  emit();
}

// ---- Journal ----
export function setJournal(dateISO, patch) {
  const prev = state.journal[dateISO] || { best: "", learned: "" };
  state = { ...state, journal: { ...state.journal, [dateISO]: { ...prev, ...patch } } };
  emit();
}

// Date of the n-th occurrence of a series (null if it can't be computed)
function nthOccurrenceISO(t, n) {
  if (!t.date || !n) return null;
  if (t.recurrence === "daily") return addDaysISO(t.date, n - 1);
  if (t.recurrence === "weekly") return addDaysISO(t.date, (n - 1) * 7);
  const base = new Date(t.date + "T00:00:00");
  let count = 1;
  let d = 0;
  while (count < n && d < 7000) {
    d++;
    const dow = (base.getDay() + d) % 7;
    if (dow !== 0 && dow !== 6) count++;
  }
  return addDaysISO(t.date, d);
}

// 1-based position of a matching day in the series (days deleted one by one still count)
function occurrenceNumber(recurrence, base, diffDays) {
  if (diffDays === 0) return 1;
  if (recurrence === "daily") return diffDays + 1;
  if (recurrence === "weekly") return diffDays / 7 + 1;
  // weekdays: the start day + every Mon-Fri in (base, target]
  let n = Math.floor(diffDays / 7) * 5;
  const startDow = base.getDay();
  for (let i = 1; i <= diffDays % 7; i++) {
    const dow = (startDow + Math.floor(diffDays / 7) * 7 + i) % 7;
    if (dow !== 0 && dow !== 6) n++;
  }
  return n + 1;
}

// ---- Selectors ----
export function tasksForDate(dateISO) {
  const tasks = state.tasks.flatMap((t) => {
    const isRecurring = t.recurrence && t.recurrence !== "none";
    if (!isRecurring) {
      if (t.date === dateISO) return [{ ...t, _occurrenceDate: dateISO }];
      return [];
    }
    if (!t.date) return [];
    const base = new Date(t.date + "T00:00:00");
    const target = new Date(dateISO + "T00:00:00");
    if (target < base) return [];
    const diffDays = Math.round((target - base) / (1000 * 60 * 60 * 24));
    let matches = diffDays === 0;
    if (!matches) {
      if (t.recurrence === "daily") matches = true;
      else if (t.recurrence === "weekly") matches = diffDays % 7 === 0;
      else if (t.recurrence === "weekdays") { const dow = target.getDay(); matches = dow !== 0 && dow !== 6; }
    }
    if (!matches) return [];
    if (t.recurrenceUntil && dateISO > t.recurrenceUntil) return [];
    if (t.recurrenceCount && occurrenceNumber(t.recurrence, base, diffDays) > t.recurrenceCount) return [];
    if ((t.excludedDates || []).includes(dateISO)) return [];
    const done = (t.completedDates || []).includes(dateISO);
    const priority = (t.priorityDates || []).includes(dateISO);
    const ov = (t.overrides || {})[dateISO] || {};
    return [{ ...t, ...ov, timeSpentSec: spentSecFor(t, dateISO), _occurrenceDate: dateISO, _hasOverride: Object.keys(ov).length > 0, done, priority }];
  });
  return tasks.sort((a, b) => {
    if ((a.time || "zz") < (b.time || "zz")) return -1;
    if ((a.time || "zz") > (b.time || "zz")) return 1;
    return (b.order || 0) - (a.order || 0);
  });
}

export function unscheduledTasks() {
  return state.tasks.filter((t) => !t.date).sort((a, b) => (b.order || 0) - (a.order || 0));
}

export const ORPHAN_CATEGORY_NAME = "Categoria da rinominare";
export const ORPHAN_CATEGORY_COLOR = "#B5B5B5";
const orphanPlaceholder = (id) => ({ id, name: ORPHAN_CATEGORY_NAME, color: ORPHAN_CATEGORY_COLOR, orphan: true });

// Categories list + one placeholder per categoryId used by tasks but missing from the list (cached by reference)
let allCatsCache = { tasks: null, categories: null, value: [] };
export function allCategories(st = state) {
  if (allCatsCache.tasks === st.tasks && allCatsCache.categories === st.categories) return allCatsCache.value;
  const known = new Set(st.categories.map((c) => c.id));
  const missing = [];
  for (const t of st.tasks || []) {
    if (t.categoryId && !known.has(t.categoryId)) { known.add(t.categoryId); missing.push(orphanPlaceholder(t.categoryId)); }
  }
  const value = missing.length ? [...st.categories, ...missing] : st.categories;
  allCatsCache = { tasks: st.tasks, categories: st.categories, value };
  return value;
}

export function findCategory(id) {
  if (!id) return state.categories[0];
  return state.categories.find((c) => c.id === id) || orphanPlaceholder(id);
}

export function prioritiesForDate(dateISO) {
  return tasksForDate(dateISO).filter((t) => t.priority);
}
